
import { IGroup } from 'office-ui-fabric-react/lib/DetailsList';
import { connectToItemCache } from './connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { groupsCollapsedFacet, allGroupsCollapsedFacet } from '../../facets/ScopeFacets';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { toggleCollapseGroup } from '../../operations/toggleCollapseGroup';
import { toggleCollapseAll } from '../../operations/toggleCollapseAll';
import { connectToItemsScope } from './connectToItemsScope';
import { childItemsFacet } from '../../facets/SetFacets';
import { IComponentDecorator, IDecoratedProps } from '../../utilities/components/Decorators';
import { readItems } from '../../operations/readItems';
import { GROUP_ITEM_LIMIT } from '../../facets/GroupFacets';

/**
 * @internal
 */
export interface IGroupsStateProps {
  groupsCollapsed?: { [key: string]: boolean } | undefined;
  allGroupsCollapsed?: boolean;
}

/**
 * @public
 */
export interface IGroupsOperationProps {
  onToggleGroupCollapsed?: (payload: {
    key: string;
    isCollapsed: boolean;
  }) => void;
  onToggleCollapseAll?: (payload: {
    groups: IGroup[];
    newAllCollapsed: boolean;
  }) => void;
  onGetGroupItemLimit?: (payload: {
    key: string;
    count: number | undefined;
    isShowingAll: boolean | undefined;
  }) => number;
}

const GROUP_ITEM_LIMIT_NOTLOADED = 3;

/**
 * @public
 */
export type IDecorateWithCurrentGroupsProps<TProps> = IDecoratedProps<TProps, IGroupsStateProps & IGroupsOperationProps, {}>;

/**
 * Binds a component to the current state of groups and callback functions to update their state.
 * @internal
 */
export function withCurrentGroups(): IComponentDecorator<IGroupsStateProps & IGroupsOperationProps, {}> {
  return <P extends IGroupsStateProps & IGroupsOperationProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, IGroupsStateProps & IGroupsOperationProps, {}>> => {
    const withItemCache = connectToItemCache((itemCache: IItemCache, props: {
      itemsScopeItemKey: string
    }): IGroupsStateProps => {
      const {
        groupsCollapsed,
        allGroupsCollapsed
      } = itemCache.demandItem({
        groupsCollapsed: groupsCollapsedFacet,
        allGroupsCollapsed: allGroupsCollapsedFacet
      }, props.itemsScopeItemKey);

      return {
        groupsCollapsed: groupsCollapsed,
        allGroupsCollapsed: allGroupsCollapsed
      };
    }, (itemCacheDispatch: IItemCacheDispatch, props: {
      itemCache: IItemCache,
      itemsScopeItemKey: string
    }): IGroupsOperationProps => {
        return {
          onToggleGroupCollapsed: (payload: {
            key: string;
            isCollapsed: boolean;
          }) => {
            itemCacheDispatch(toggleCollapseGroup({
              itemsScopeItemKey: props.itemsScopeItemKey,
              groupKey: payload.key,
              isCollapsed: payload.isCollapsed
            }));
          },
          onToggleCollapseAll: (payload: {
            groups: IGroup[];
            newAllCollapsed: boolean;
          }) => {
            itemCacheDispatch(toggleCollapseAll({
              itemsScopeItemKey: props.itemsScopeItemKey,
              newAllCollapsed: payload.newAllCollapsed,
              groups: payload.groups
            }));
          },
          onGetGroupItemLimit: (payload: {
            key: string;
            count: number | undefined;
            isShowingAll: boolean | undefined;
          }) => {
            const isChildItemsFacetResolved = itemCacheDispatch(readItems((itemCache: IItemCache) => {
              return itemCache.isItemFacetResolved(childItemsFacet, payload.key);
            }));

            let count = payload.count ? payload.count : 0;

            if (!isChildItemsFacetResolved) {
              return Math.min(count, GROUP_ITEM_LIMIT_NOTLOADED);
            } else {
              return payload.isShowingAll ? count : Math.min(count, GROUP_ITEM_LIMIT);
            }
          }
        };
      })(component);

    // @ts-ignore Need Exclude type from TypeScript 2.8.
    return connectToItemsScope()(withItemCache);
  };
}
